import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Cake,
  Package,
  Truck,
  LogOut,
  MessageCircle,
  Briefcase,
  Wallet,
  Images,
  LayoutDashboard,
  ChevronRight,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { requestManageSession } from "@/lib/manageSession";
import { MANAGER_TOKEN_KEY } from "@/lib/authSession";
import { canAccessDesignGallery, canAccessManage } from "@/lib/tsIdentity";
import { applyStaffPortalDocument, enterStaffPortal } from "@/lib/staffPortal";
import TsSessionGate from "@/components/mobile/TsSessionGate";
import TsOnboarding from "@/components/mobile/TsOnboarding";
import { TS_DEPARTMENT_LABELS } from "@/components/mobile/TsDepartmentPicker";
import {
  clearTsSession,
  completeTsOnboarding,
  filterPortionsByDepartments,
  loadTsDepartments,
  loadTsPortionUrls,
  tsAppPortionPaths,
  tsVisiblePortions,
} from "@/lib/tsIdentity";
import {
  isTeamHubHost,
  staffBrandTitle,
  teamHomePath,
  TEAM_APP_PATHS,
} from "@/lib/teamHub";
import { OPS_BASENAME } from "@/lib/opsBase";

const PINK = "#EE2677";

const PORTION_ICONS = {
  kitchen: Cake,
  driver: Truck,
  inventory: Package,
  chat: MessageCircle,
  salary: Wallet,
  we: Briefcase,
};

/**
 * Short display labels for this page only.
 *
 * The canonical name stays "Salary & Attendance" — that is what the backend
 * routes, the PWA manifest and guard-sa-app.mjs all call the app. But at the
 * 4-up width a card gives its title roughly 11 characters, so the long name
 * wrapped to two lines, which made its whole grid row taller than the row
 * above. Card titles are a nav label, not a product name; keep them short.
 */
const TILE_LABELS = {
  salary: "Salary",
};

// Was a ternary chain whose else-branch handed Salary the Inventory line
// ("Stock bins & inventory"). A lookup cannot silently mislabel a new portion.
const PORTION_SUBTITLES = {
  kitchen: "Production: baker, froster, decorator",
  driver: "Delivery board & handoff",
  inventory: "Stock bins & inventory",
  chat: "WhatsApp chat report handoff",
  salary: "Salary & attendance records",
  we: "Team tasks, time & work ethic",
};

/**
 * Per-app accent for the icon chip. The page carries almost no data, so the
 * icon colour is what makes a tile findable at a glance — eight identical pink
 * chips read as one block. Hues are held at a similar lightness and used only
 * inside the chip, so the page stays quiet and the brand pink still leads.
 */
const TILE_ACCENTS = {
  manage: "#5B6472",
  kitchen: PINK,
  driver: "#B4791B",
  inventory: "#0F7B6C",
  chat: "#2F7D4F",
  salary: "#6D5BD0",
  we: "#2C6BB8",
  chats: "#2F7D4F",
  designs: "#C2417A",
};

/** `Arup Oudyal` -> `AO`; falls back to the first character. */
function initialsOf(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * One launch card: icon, title, one-line description, chevron, whole card
 * clickable. Every tile on this page goes through here so the icon chip, type
 * scale and hover/focus/disabled states cannot drift apart per call site.
 *
 * Rendered as an anchor, not a button, so a middle-click or ctrl/cmd-click
 * opens the app in a new tab the way staff expect of anything that looks like
 * a link. Plain left clicks are still handled in-app: react-router's Link does
 * that for route targets, and the `onActivate` form below does it for Ops,
 * whose click is a token exchange rather than a navigation.
 */
function TsHomeTile({ icon: Icon, label, sub, to, href, onActivate, accent, disabled = false, testId }) {
  // The accent rides on the tile as custom properties rather than as an inline
  // style on the icon, so the hover rules further down can reach it too — an
  // inline style on a child is invisible to `.ts-home-tile:hover`.
  const accentVars = accent
    ? { "--tile-accent": accent, "--tile-accent-soft": `${accent}14`, "--tile-accent-ring": `${accent}66` }
    : undefined;

  const inner = (
    <>
      <span className="ts-home-tile-icon">
        <Icon size={24} aria-hidden />
      </span>
      <span className="ts-home-tile-text">
        <span className="ts-home-tile-label">{label}</span>
        <span className="ts-home-tile-sub">{sub}</span>
      </span>
      <ChevronRight className="ts-home-tile-chevron" size={18} aria-hidden />
    </>
  );

  if (to) {
    return (
      <Link className="ts-home-tile" to={to} style={accentVars} data-testid={testId}>
        {inner}
      </Link>
    );
  }

  // Ops: the href is real so the browser can open it in a new tab, but a plain
  // left click is intercepted to trade the TS session for a manager token
  // first, which is faster than letting the new page do it on arrival.
  const handleClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (!disabled) onActivate();
  };

  return (
    <a
      className="ts-home-tile"
      href={href}
      onClick={handleClick}
      style={accentVars}
      aria-disabled={disabled || undefined}
      data-testid={testId}
    >
      {inner}
    </a>
  );
}

function TsHomeBody({ identity: initialIdentity }) {
  const [identity, setIdentity] = useState(initialIdentity);
  const [err, setErr] = useState("");
  const [showReviewSetup, setShowReviewSetup] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);

  useEffect(() => {
    const home = teamHomePath();
    enterStaffPortal("ts", isTeamHubHost() ? home : "/ts");
    applyStaffPortalDocument("ts");
  }, []);

  useEffect(() => {
    setIdentity(initialIdentity);
  }, [initialIdentity]);

  const handleLogout = () => {
    clearTsSession();
    window.location.href = isTeamHubHost() ? teamHomePath() : "/ts";
  };

  /**
   * Trades this TS session for a short manager token, so an admin never types
   * the shared password. The server re-resolves the role instead of trusting
   * the token claim, so someone demoted since login is refused here even while
   * holding a token that still says admin.
   *
   * Ops is at /ops on this origin now, so the token is simply stored. The
   * HttpOnly parent-domain cookie this used to need existed only because Team
   * and Manage were separate origins and localStorage is per-origin; it went
   * away with the origin split.
   *
   * A full load rather than navigate(): /ops mounts the router under a
   * different basename, and that only takes effect on a fresh mount.
   */
  const openOps = async () => {
    setManageBusy(true);
    setErr("");
    try {
      const token = await requestManageSession();
      localStorage.setItem(MANAGER_TOKEN_KEY, token);
      window.location.href = OPS_BASENAME;
    } catch (ex) {
      setErr(ex.message || "Could not open Ops");
      toast.error(ex.message || "Could not open Ops");
      setManageBusy(false);
    }
  };

  const handleReviewSave = ({ name, departments }) => {
    if (!identity) return;
    setReviewBusy(true);
    setErr("");
    try {
      const depts =
        identity.is_ts_admin || (identity.available_departments || []).length <= 1
          ? identity.available_departments || []
          : departments;
      completeTsOnboarding(identity, { name, departments: depts });
      setIdentity({ ...identity, name: (name || identity.name || "").trim() });
      setShowReviewSetup(false);
    } catch (ex) {
      setErr(ex.message || "Could not save setup");
    } finally {
      setReviewBusy(false);
    }
  };

  if (showReviewSetup && identity) {
    return (
      <TsOnboarding
        mode="review"
        identity={identity}
        initialDepartments={loadTsDepartments()}
        onComplete={handleReviewSave}
        onCancel={() => setShowReviewSetup(false)}
        busy={reviewBusy}
      />
    );
  }

  const allPortions = tsVisiblePortions(identity, loadTsPortionUrls());
  const portions = filterPortionsByDepartments(allPortions, loadTsDepartments(), identity);
  // A staff-role person still sees Salary, so an empty grid means their role
  // grants nothing at all — say so rather than showing a blank page.
  const hasNoTiles =
    !portions.length && !canAccessManage(identity) && !canAccessDesignGallery(identity) && !identity?.can_chat;
  const displayName = (identity?.name || "").trim();
  const selectedDepts = loadTsDepartments();
  const deptSummary =
    identity?.is_ts_admin || !selectedDepts.length
      ? null
      : selectedDepts.map((k) => TS_DEPARTMENT_LABELS[k] || k).join(" · ");
  const initials = initialsOf(displayName);
  // Employee ID and departments used to be two stacked centred blocks; one
  // dot-separated line carries the same facts in a third of the height.
  const metaLine = [identity?.employee_id ? `ID ${identity.employee_id}` : null, deptSummary]
    .filter(Boolean)
    .join(" · ");
  const canReviewSetup = Boolean(deptSummary) || !identity?.is_ts_admin;
  const showWho = Boolean(displayName || metaLine || canReviewSetup);

  return (
    <div className="ts-home">
      <style>{`
        /* .ts-home is the full-bleed page; .ts-home-shell is the centred content
           column. The split is what leaves room for the global staff navigation
           rail: it lands later as a sibling of the shell inside .ts-home, so the
           content column keeps its own width cap and does not have to be
           re-derived from the page padding. The rail is not built here. */
        .ts-home {
          min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
          font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          padding: 16px 14px 32px;
        }
        .ts-home * { box-sizing: border-box; }
        .ts-home-shell { max-width: 480px; margin: 0 auto; }
        /* Hero: brand block and identity block. Was five centred lines of text
           stacked down the page; now two groups that sit side by side as soon
           as there is room, so the page starts at the apps instead of scrolling
           past a column of small print. */
        .ts-home-header {
          display: flex; flex-direction: column; align-items: center; gap: 14px;
          text-align: center; margin: 12px 0 24px;
        }
        /* Just "Team". The grey "The Table Top" kicker that used to sit beside
           it told a staff member, already inside their own staff portal,
           nothing they needed. It still fronts the login, signup and
           pending-approval screens, where the reader may not know where they
           have landed. */
        .ts-home-brand {
          display: flex; flex-direction: row; align-items: baseline;
          justify-content: center;
        }
        .ts-home-title {
          font-size: 34px; font-weight: 900; color: ${PINK}; margin: 0; line-height: 1;
        }
        .ts-home-who {
          display: flex; align-items: center; gap: 12px; text-align: left;
          background: #fff; border: 1px solid rgba(45,45,45,0.07); border-radius: 16px;
          padding: 10px 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.03); max-width: 100%;
        }
        .ts-home-avatar {
          width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: #FFF0F6; color: ${PINK}; font-size: 14px; font-weight: 900;
          letter-spacing: 0.02em;
        }
        .ts-home-who-text { min-width: 0; }
        .ts-home-greet {
          margin: 0; font-size: 15px; font-weight: 800; color: #2D2D2D; line-height: 1.2;
        }
        /* Employee ID and departments were two stacked blocks; one dot-separated
           line says the same thing in a third of the height. */
        .ts-home-meta {
          margin: 2px 0 0; font-size: 12px; font-weight: 700; color: rgba(45,45,45,0.5);
          line-height: 1.3; overflow-wrap: anywhere;
        }
        .ts-home-setup-links { display: flex; align-items: center; }
        .ts-home-setup-link {
          border: none; background: transparent; color: ${PINK};
          font-size: 13px; font-weight: 800; cursor: pointer; padding: 4px 0;
        }
        .ts-home-grid { display: grid; gap: 12px; align-items: stretch; }
        .ts-home-tile {
          display: flex; align-items: center; gap: 14px;
          background: #fff; border-radius: 18px; padding: 18px 16px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05); cursor: pointer; text-align: left; width: 100%;
          color: inherit; text-decoration: none;
          /* Every tile carries the same border width. When the Ops tile alone
             had a 2px border and the rest had none, box-sizing took those 2px
             out of the padding, so its icon and text sat 2px in from every
             other card and its row read as differently spaced. A variant may
             change the colour here; it must not change the width. */
          border: 1px solid rgba(45,45,45,0.07);
          /* Grid rows stretch, so a short card next to a wrapping one still
             fills the row instead of floating at the top of it. */
          height: 100%;
          transition: box-shadow 140ms ease, transform 140ms ease, border-color 140ms ease;
        }
        .ts-home-tile-icon {
          width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          /* Per-app accent, set on the tile by TsHomeTile. The fallbacks are
             the brand pink, for a tile that declares no accent. */
          color: var(--tile-accent, ${PINK});
          background: var(--tile-accent-soft, #FFF0F6);
          transition: transform 140ms ease;
        }
        .ts-home-tile-text { min-width: 0; flex: 1; }
        .ts-home-tile-label { display: block; font-size: 18px; font-weight: 800; color: #2D2D2D; }
        .ts-home-tile-sub {
          display: block; font-size: 13px; font-weight: 600;
          color: rgba(45,45,45,0.5); margin-top: 2px;
        }
        /* Says "this opens something" without adding a second click target. */
        .ts-home-tile-chevron {
          flex-shrink: 0; color: rgba(45,45,45,0.25);
          transition: transform 140ms ease, color 140ms ease;
        }
        .ts-home-logout {
          margin-top: 24px; width: 100%; border: none; background: transparent;
          color: rgba(45,45,45,0.5); font-size: 14px; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px;
        }
        .ts-home-err {
          background: #FEE2E2; color: #B91C1C; border-radius: 12px; padding: 12px;
          font-size: 14px; font-weight: 600; margin-bottom: 12px;
        }
        .ts-home-empty {
          background: #fff; border-radius: 14px; padding: 18px 16px; margin: 0 0 12px;
          font-size: 14px; font-weight: 600; color: rgba(45,45,45,0.6); text-align: center;
        }
        .ts-home-tile[aria-disabled="true"] { opacity: 0.6; cursor: default; }
        .ts-home-tile:focus-visible { outline: 2px solid ${PINK}; outline-offset: 2px; }
        /* Pointer-only: a hover style on touch sticks after the tap.
           The card lifts, the border picks up that app's accent and the icon
           and chevron move — enough that the tile under the cursor is obvious
           at a glance across a 4-up grid. */
        @media (hover: hover) {
          .ts-home-tile:hover:not([aria-disabled="true"]) {
            box-shadow: 0 10px 24px rgba(0,0,0,0.13);
            border-color: var(--tile-accent-ring, rgba(45,45,45,0.28));
            transform: translateY(-3px);
          }
          .ts-home-tile:hover .ts-home-tile-icon { transform: scale(1.06); }
          .ts-home-tile:hover .ts-home-tile-chevron {
            color: var(--tile-accent, rgba(45,45,45,0.5)); transform: translateX(4px);
          }
        }
        .ts-home-tile:active:not([aria-disabled="true"]) {
          transform: translateY(0); box-shadow: 0 1px 6px rgba(0,0,0,0.07);
        }
        @media (prefers-reduced-motion: reduce) {
          .ts-home-tile, .ts-home-tile-chevron, .ts-home-tile-icon { transition: none; }
        }

        /* Tablet and up: drop the phone width cap for a centred desktop column
           and let the launch cards share rows. The card itself is unchanged —
           it gets wider, not taller, so this stays a launcher, not a dashboard. */
        @media (min-width: 768px) {
          .ts-home { padding: 24px 24px 40px; }
          .ts-home-shell { max-width: 1160px; }
          .ts-home-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .ts-home-logout { max-width: 260px; margin-left: auto; margin-right: auto; }
          /* Brand left, identity right — the hero becomes one band instead of
             a column, which is most of the vertical saving on desktop. */
          .ts-home-header {
            flex-direction: row; align-items: center; justify-content: space-between;
            text-align: left; gap: 20px; margin: 8px 0 28px;
          }
          /* Room to spare here, so the wordmark carries the page. */
          .ts-home-brand { justify-content: flex-start; }
          .ts-home-title { font-size: 44px; }
          /* Equal rows, not just equal cards within a row. Cards already
             stretch to their row; the rows themselves still sized to content,
             so one title wrapping to two lines made that entire row taller
             than the one above it. */
          .ts-home-grid { grid-auto-rows: 1fr; }
        }
        @media (min-width: 1120px) {
          .ts-home-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (min-width: 1600px) {
          .ts-home-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
      `}</style>

      <div className="ts-home-shell">
        <header className="ts-home-header">
          <div className="ts-home-brand">
            <h1 className="ts-home-title">{staffBrandTitle()}</h1>
          </div>
          {showWho ? (
            <div className="ts-home-who">
              <span className="ts-home-avatar" aria-hidden>
                {initials || <User size={18} />}
              </span>
              <div className="ts-home-who-text">
                {displayName ? <p className="ts-home-greet">{displayName}</p> : null}
                {metaLine ? <p className="ts-home-meta">{metaLine}</p> : null}
                {canReviewSetup ? (
                  <div className="ts-home-setup-links">
                    <button type="button" className="ts-home-setup-link" onClick={() => setShowReviewSetup(true)}>
                      Review setup
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </header>

        {err ? <p className="ts-home-err">{err}</p> : null}

        {hasNoTiles ? (
          <p className="ts-home-empty" data-testid="ts-home-empty">
            No apps are assigned to you yet. Ask a manager to set your access role.
          </p>
        ) : null}

        <div className="ts-home-grid">
          {/* Manage sits first: it used to be the parent of everything, so for an
              admin it is the most likely destination on this screen. It no longer
              carries a highlight — nearly nobody who sees this page lacks it, so
              the emphasis bought nothing and made its row read differently. */}
          {canAccessManage(identity) ? (
            <TsHomeTile
              icon={LayoutDashboard}
              accent={TILE_ACCENTS.manage}
              label="Ops"
              sub={manageBusy ? "Opening…" : "Office console: orders, customers, finance"}
              disabled={manageBusy}
              href={OPS_BASENAME}
              onActivate={openOps}
              testId="ts-home-tile-manage"
            />
          ) : null}
          {portions.map(({ key, label, path }) => (
            <TsHomeTile
              key={key}
              icon={PORTION_ICONS[key] || Package}
              accent={TILE_ACCENTS[key]}
              label={TILE_LABELS[key] || label}
              sub={PORTION_SUBTITLES[key] || ""}
              to={path}
              testId={`ts-home-tile-${key}`}
            />
          ))}
          {identity?.can_chat ? (
            <TsHomeTile
              icon={MessageCircle}
              accent={TILE_ACCENTS.chats}
              label="Chats"
              sub="WhatsApp inbox"
              to={isTeamHubHost() ? TEAM_APP_PATHS.tc : "/ts/app/tc"}
              testId="ts-home-tile-chats"
            />
          ) : null}
          {canAccessDesignGallery(identity) ? (
            <TsHomeTile
              icon={Images}
              accent={TILE_ACCENTS.designs}
              label="Designs"
              sub="Cake design gallery"
              to={tsAppPortionPaths().gallery || "/ts/app/gallery"}
              testId="ts-home-tile-designs"
            />
          ) : null}
        </div>

        <button type="button" className="ts-home-logout" onClick={handleLogout}>
          <LogOut size={16} aria-hidden />
          {isTeamHubHost() ? "Sign out" : "Sign out of TS"}
        </button>
      </div>
    </div>
  );
}

export default function TsHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleReady = () => {
    if (!isTeamHubHost()) return;
    const raw = (searchParams.get("next") || "").trim();
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("://") || raw === "/") return;
    navigate(raw, { replace: true });
  };

  return (
    <TsSessionGate onReady={handleReady}>
      {({ identity }) => (identity ? <TsHomeBody identity={identity} /> : null)}
    </TsSessionGate>
  );
}
